import React from "react";
import { Box, Typography } from "@mui/material";
import { T, cardSx, MOTION } from "utils/backrrTokens";
import BarRow from "./BarRow";
import InfoHint from "./InfoHint";

// Paid and free are one hue apart in LIGHTNESS, not in colour: the pair has to
// survive every colour-vision deficiency, and these two values sit side by side
// inside a single bar where hue alone would not separate them.
const PAID = "#5925DC";
const FREE = "#E9D7FE";
// A third step on the same lightness ramp, for startups already paying before
// the selected range.
const PAID_EARLIER = "#9E77ED";

const ASSESSMENT_KEYS = [
  { label: "Paid", color: PAID },
  { label: "Free", color: FREE },
];

// The same badge the subscriptions listing and the plan settings draw, so one
// plan looks like one plan wherever an admin meets it. A customer never sees
// "Plus V2" — the version is metadata about the plan, not part of its name —
// which is why it is a marker rather than two words of the title.
const VersionBadge = ({ version }) => {
  const isCurrent = version > 1;
  return (
    <Box
      component="span"
      sx={{
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1,
        padding: "3px 5px",
        borderRadius: "4px",
        flexShrink: 0,
        backgroundColor: isCurrent ? "#EEF2FF" : "#F3F4F6",
        color: isCurrent ? "#4338CA" : "#6B7280",
        border: `1px solid ${isCurrent ? "#C7D2FE" : "#E5E7EB"}`,
      }}
    >
      {`V${version}`}
    </Box>
  );
};

const Legend = ({ keys }) => (
  <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
    {keys.map((k) => (
      <Box key={k.label} display="flex" alignItems="center" gap={1}>
        <Box sx={{ width: 9, height: 9, borderRadius: "3px", backgroundColor: k.color, flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle }}>{k.label}</Typography>
      </Box>
    ))}
  </Box>
);

// Disjoint parts that SUM to the whole, so the bar can be read as a proportion.
// Anything with a zero count is left out of the bar but kept in the legend, so a
// missing segment never looks like a missing category.
const SplitBar = ({ parts, label }) => {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <Box
      role="img"
      aria-label={`${label}: ${parts.map((p) => `${p.value} ${p.label.toLowerCase()}`).join(", ")}`}
      sx={{ display: "flex", gap: "3px", height: 10, borderRadius: "5px", overflow: "hidden" }}
    >
      {total === 0 ? (
        <Box sx={{ flex: 1, backgroundColor: T.grid }} />
      ) : (
        parts
          .filter((p) => p.value > 0)
          .map((p) => (
            <Box key={p.label} sx={{ width: `${(p.value / total) * 100}%`, backgroundColor: p.color, ...MOTION.sx }} />
          ))
      )}
    </Box>
  );
};

const Card = ({ title, subtitle, hint, action, children }) => (
  <Box sx={{ ...cardSx, padding: "14px 16px", display: "flex", flexDirection: "column" }}>
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
      <Box display="flex" alignItems="center">
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: T.ink }}>{title}</Typography>
        {hint && <InfoHint text={hint} label={title} />}
      </Box>
      {action}
    </Box>
    {subtitle && (
      <Typography sx={{ fontSize: "0.75rem", color: T.inkSubtle, marginTop: 1 }}>{subtitle}</Typography>
    )}
    {children}
  </Box>
);

const PlanMixCard = ({ data }) => {
  const rows = data?.by_plan?.rows ?? [];
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  const u = data?.users ?? {};
  const paid = u.paid ?? 0;
  const paidEarlier = u.paid_earlier ?? 0;
  const free = u.free ?? 0;
  const rate = data?.subscription_rate?.value;

  // "Paid earlier" only earns a segment when it exists. Without it the bar is
  // the plain paid/free split that was asked for; with it, the bar still adds
  // up to every startup instead of quietly dropping the ones who have been
  // paying since before this range.
  const parts = [
    { label: "Paid", value: paid, color: PAID },
    ...(paidEarlier > 0 ? [{ label: "Paid Earlier", value: paidEarlier, color: PAID_EARLIER }] : []),
    { label: "Free / Lite", value: free, color: FREE },
  ];

  return (
    <Card
      title="Plan Mix"
      subtitle={`${data?.by_plan?.total ?? 0} startups bought a plan in this range`}
      hint="The plan bars count startups that bought each plan inside the selected range, so they move with the filter like every other chart here. The split bar above them is the only figure on this page that cannot: its population is EVERY startup, and 'Paid Earlier' means 'was already paying when this range began' — rebase it on startups created in the range and that segment is always zero, because a startup cannot have subscribed before it existed. So the range moves its Paid segment, and the total it is measured against stays lifetime."
      action={<Legend keys={parts} />}
    >
      <Box sx={{ marginTop: 2 }}>
        <SplitBar parts={parts} label="All startups, to date" />
        <Box display="flex" justifyContent="space-between" gap={1} sx={{ marginTop: 1 }} flexWrap="wrap">
          {parts.map((p) => (
            <Typography key={p.label} sx={{ fontSize: "0.78rem", color: T.inkMuted }}>
              {p.label} <strong style={{ fontVariantNumeric: "tabular-nums" }}>{p.value}</strong>
            </Typography>
          ))}
        </Box>
        <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle, marginTop: 1 }}>
          of {u.total ?? 0} startups
          {rate === null || rate === undefined
            ? ""
            : ` · ${rate}% of those who took an assessment went on to pay`}
        </Typography>
      </Box>

      <Box sx={{ marginTop: 2, paddingTop: 2, borderTop: `1px solid ${T.border}` }}>
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: "0.78rem", color: T.inkSubtle, padding: 2, textAlign: "center" }}>
            No active subscriptions yet.
          </Typography>
        ) : (
          rows.map((r) => (
            <BarRow
              key={r.label}
              label={r.label}
              count={r.count}
              max={max}
              color={FREE}
              badge={r.show_version ? <VersionBadge version={r.version} /> : null}
            />
          ))
        )}
      </Box>
    </Card>
  );
};

const AssessmentsCard = ({ data }) => {
  const rows = data?.assessments?.rows ?? [];
  // Each row's bar is scaled against the BIGGEST row, not stretched to the
  // card. Full-width bars made 1 fundability report and 2 pitch decks look like
  // the same amount of activity; now length carries volume and the split inside
  // still carries paid against free.
  const max = rows.reduce((m, r) => Math.max(m, r.total), 0);

  return (
    <Card
      title="AI Insights"
      subtitle={`${data?.assessments?.total ?? 0} in this range`}
      hint="Fundability and pitch-deck reports generated in this range, split by whether the startup unlocked the full result. Both kinds share one collection and are told apart by type, so the two rows are counted the same way."
      action={<Legend keys={ASSESSMENT_KEYS} />}
    >
      <Box sx={{ marginTop: 2, display: "grid", gap: 2 }}>
        {rows.map((r) => (
          <Box key={r.key}>
            <Box display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 500, color: T.inkMuted }}>
                {r.label}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                {r.total}
              </Typography>
            </Box>
            <Box sx={{ marginTop: 1, width: max > 0 ? `${Math.max((r.total / max) * 100, 2)}%` : "100%", ...MOTION.sx }}>
              <SplitBar
                parts={[
                  { label: "Paid", value: r.paid, color: PAID },
                  { label: "Free", value: r.free, color: FREE },
                ]}
                label={r.label}
              />
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: T.inkSubtle, marginTop: 1 }}>
              {r.paid} paid · {r.free} free
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

const MonetizationBand = ({ data, isLoading, isError }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
        <Box className="skeleton" sx={{ ...cardSx, height: "15rem" }} />
        <Box className="skeleton" sx={{ ...cardSx, height: "15rem" }} />
      </Box>
    );
  }

  // The monetization half lives in a different service from the rest of this
  // page, so it can fail on its own. Saying so beats rendering zeroes that look
  // like a business with no customers.
  if (isError) {
    return (
      <Box sx={{ ...cardSx, padding: 3, textAlign: "center" }}>
        <Typography sx={{ fontSize: "0.8rem", color: T.inkSubtle }}>
          Plan and assessment figures could not be loaded.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 2,
        // stretch, so both cards finish on the same line. With "start" the
        // shorter card left a ragged gap above the next row — the space between
        // sections then read as two different sizes depending on the column.
        alignItems: "stretch",
      }}
    >
      <PlanMixCard data={data} />
      <AssessmentsCard data={data} />
    </Box>
  );
};

export default MonetizationBand;
